import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { API, errMsg } from "@/lib/api";

const EMPTY = { name: "", text: "", category: "", date: "", active: true, order: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => API.get("/admin/testimonials").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return toast.error("Name and testimonial text are required");
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (dialog?.id) await API.patch(`/admin/testimonials/${dialog.id}`, payload);
      else await API.post("/admin/testimonials", payload);
      toast.success("Testimonial saved");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">Testimonials</h1>
        <Button className="rounded-full" onClick={() => { setForm(EMPTY); setDialog({}); }} data-testid="add-testimonial-btn">
          <Plus className="mr-2 h-4 w-4" /> Add testimonial
        </Button>
      </div>

      {!items ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-center py-16 border rounded-xl" data-testid="testimonials-admin-empty">No testimonials yet.</p>
      ) : (
        <div className="space-y-3" data-testid="testimonials-admin-list">
          {items.map((t) => (
            <Card key={t.id} data-testid={`admin-testimonial-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium">{t.name} {t.category && <span className="text-muted-foreground font-normal text-sm">· {t.category}</span>}</p>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">“{t.text}”</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={!!t.active} onCheckedChange={async () => {
                      try {
                        await API.patch(`/admin/testimonials/${t.id}`, { name: t.name, text: t.text, category: t.category || "", date: t.date || "", active: !t.active, order: t.order || 0 });
                        load();
                      } catch (e) { toast.error(errMsg(e)); }
                    }} data-testid={`testimonial-active-${t.id}`} aria-label="Visible" />
                    <Button variant="ghost" size="icon" onClick={() => { setForm({ name: t.name, text: t.text, category: t.category || "", date: t.date || "", active: !!t.active, order: t.order || 0 }); setDialog(t); }} data-testid={`edit-testimonial-${t.id}`} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={async () => {
                      try {
                        await API.delete(`/admin/testimonials/${t.id}`);
                        toast.success("Deleted");
                        load();
                      } catch (e) { toast.error(errMsg(e)); }
                    }} data-testid={`delete-testimonial-${t.id}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle>{dialog?.id ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Customer name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Testimonial *</Label>
              <Textarea rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="testimonial-text-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category (optional)</Label>
                <Input placeholder="e.g. Weddings" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="testimonial-category-input" />
              </div>
              <div className="space-y-2">
                <Label>Date (optional)</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="testimonial-date-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Visible on website</Label>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="testimonial-active-switch" />
            </div>
            <Button className="w-full rounded-full" onClick={save} data-testid="save-testimonial-btn">Save testimonial</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
